import { trigger } from '@angular/animations';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
  AssetDto,
  AssetStatus,
  DisplayGuestDto,
  DisplayUser,
  GalleryDto,
  GalleryFeedbackOptions,
} from '@nusign/picflow-backend-types';
import { Observable, Subject, filter, map, of, takeUntil } from 'rxjs';
import { fadeUpIfSubtle } from '../../../animations/fade-up-subtle.if.animation';
import { Rect } from '../../../classes/rect.class';
import { AssetStatusButtonComponent } from '../../../gallery/components/asset-status-button/asset-status-button.component';
import { AssetStatusColorPipe } from '../../../gallery/pipes/asset-status-color.pipe';
import { AssetStatusIconPipe } from '../../../gallery/pipes/asset-status-icon.pipe';
import { AssetPageDataService } from '../../../gallery/services/asset-page-data.service';
import { GROWTHBOOK } from '../../../growthbook';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { AssetFeedbackService } from '../../../services/asset-feedback.service';
import { AssetService } from '../../../services/asset.service';
import { StateService } from '../../../services/state.service';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { AssetVersionUrlPipe } from '../../../shared/pipes/asset-version-url.pipe';
import { CanDisplayAssetPipe } from '../../pipes/can-display-asset.pipe';
import { VideoViewerComponent } from '../video-viewer/video-viewer.component';

@Component({
  selector: 'app-layout-asset',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './layout-asset.component.html',
  styleUrl: './layout-asset.component.scss',
  animations: [trigger('feedback', fadeUpIfSubtle), trigger('feedbackHints', fadeUpIfSubtle)],
  imports: [
    AssetVersionUrlPipe,
    AsyncPipe,
    VideoViewerComponent,
    CanDisplayAssetPipe,
    SvgComponent,
    LoadingIndicatorComponent,
    AssetStatusColorPipe,
    AssetStatusIconPipe,
    ButtonDirective,
    AssetStatusButtonComponent,
    TooltipDirective,
  ],
})
export class LayoutAssetComponent implements OnInit, OnDestroy {
  public growthBook = inject(GROWTHBOOK);
  private check = inject(ChangeDetectorRef);
  private assetFeedbackService = inject(AssetFeedbackService);
  private assetService = inject(AssetService);
  private assetPageDataService = inject(AssetPageDataService);
  private state = inject(StateService);
  @Input({ required: true }) asset: AssetDto;
  @Input({ required: true }) gallery: GalleryDto;
  @Input({ required: true }) isDesktop: boolean;
  @Input({ required: true }) hovering: boolean;
  @Input({ required: true }) dimensions: Rect;
  @Input({ required: true }) accessLevel: GalleryAccessLevel;
  @Input({ required: true }) approvedAssetCount: number;
  @Input({ required: true }) clientMode: boolean;
  @Input() allowedAndEnabledFeedbackOptions: GalleryFeedbackOptions[] = [];
  @Input() preview: string;

  destroyed$ = new Subject<void>();

  favoritedUsersString$: Observable<string> = of('Loading...');

  ngOnInit(): void {
    this.assetService.updates
      .pipe(
        takeUntil(this.destroyed$),
        filter(asset => asset?.id === this.asset?.id)
      )
      .subscribe(() => {
        this.check.detectChanges();
      });
  }

  toggleLike(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.assetFeedbackService.toggleLike(this.asset, this.gallery);
  }

  updateAssetStatus(status: AssetStatus) {
    this.assetFeedbackService.updateAssetStatus(this.asset, this.gallery, status, true);
  }

  statusClick(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  onTimeChange() {
    this.check.detectChanges();
  }

  openFavoritedUsers() {
    if (this.accessLevel !== 'edit') return 'Favorite';
    if (this.clientMode) return 'Favorite';
    this.favoritedUsersString$ = this.assetPageDataService.getAssetLikeUsers(this.asset.id).pipe(
      map(users => {
        const currentUserId = this.state.user?.id || this.state.guest?.id;
        return users
          .map(user => {
            const isCurrentUser = user.id === currentUserId;
            const name = this.getUserName(user);
            return name + (isCurrentUser ? ' (You)' : '');
          })
          .join('<br>');
      }),
      map(string => {
        if (string) return string;
        return 'Favorite';
      })
    );
  }
  private getUserName(
    user: (DisplayUser | DisplayGuestDto) & {
      id: string;
    }
  ) {
    if ('name' in user) return user.name;
    if ('first_name' in user) {
      return user.first_name + (user.last_name ? ' ' + user.last_name : '');
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
